import React, { PureComponent, ReactNode } from 'react';
import { Flex } from '../../common/Flex';
import FlightTable, {
    FLIGHT_SELECTED_COLUMN,
    FlightColumn,
    StandardFlightColumn,
    StandardFlightColumnV2,
} from '../../table/FlightTable';
import KeyUtils from '../../../common/util/KeyUtils';
import {
    CountType,
    FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    FlightDto,
    FlightId,
    FlightListAlgorithm,
    FlightListByTvRequestDto,
    StandardFlightFieldType,
    TimePeriod,
    TrafficType,
} from '../../../model/CORE';
import TrafficCountUtils from '../../../common/util/TrafficCountUtils';
import { ServiceFactory } from '../../../service/ServiceFactory';
import { Button, notification, Space } from 'antd';
import InputTime from '../../../common/input/InputTime';
import SelectEnum from '../../../common/input/SelectEnum';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection } from '../../../common/table/ManagedTable';
import { SelectionLabel } from '../../common/SelectionLabel';
import Utils from '../../../common/util/Utils';
import { UpdateFlightSelection } from '../../../common/eventbus/Events';
import AppContext from '../../../common/context/AppContext';
import { ModalPanelProps } from '../DashboardModal';
import FlightUtils from '../../../common/util/FlightUtils';
import { RenderFunction } from 'antd/lib/_util/getRenderPropValue';
import { ModalHandler } from '../ModalHandler';
import ConditionalRender from 'app/common/components/ConditionalRender';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import AppConstants from 'app/common/context/AppConstants';

export declare type FlightListButtonsRenderFunction = (selection: Array<FlightDto>) => React.ReactNode;

export interface FlightListPanelProps extends ModalPanelProps {
    tvId: string;
    countType: CountType;
    trafficWindow: TimePeriod;
    trafficType: TrafficType;
    defaultSelectedFlights?: Array<FlightId>;
    extraButtons?: ReactNode[] | RenderFunction | FlightListButtonsRenderFunction;
}

interface FlightListPanelState {
    flights: Array<FlightDto>;
    selection: Array<FlightDto>;
}

export class FlightListPanel<P extends FlightListPanelProps = FlightListPanelProps> extends PureComponent<
    P,
    FlightListPanelState
> {
    SIMPLE_FLIGHT_COLUMNS: FlightColumn[] = [
        FLIGHT_SELECTED_COLUMN,
        StandardFlightColumn.ENTRY_TIME,
        StandardFlightColumn.EXIT_TIME,
        StandardFlightColumn.ARCID,
        StandardFlightColumn.ATYPE,
        StandardFlightColumn.ADEP,
        StandardFlightColumn.ADES,
        StandardFlightColumn.IOBT,
        StandardFlightColumn.CFMU_TYPE,
        StandardFlightColumn.ATOT_TTOT,
        StandardFlightColumn.CTOT,
        StandardFlightColumn.ATOT,
        StandardFlightColumn.xTA,
        StandardFlightColumn.MPR,
        StandardFlightColumn.DELAY,
        StandardFlightColumn.CONVECTION_IMPACT,
    ];
    SIMPLE_FLIGHT_COLUMNS_V2: FlightColumn[] = [
        FLIGHT_SELECTED_COLUMN,
        StandardFlightColumn.ENTRY_TIME,
        StandardFlightColumn.EXIT_TIME,
        StandardFlightColumnV2.ARCID,
        StandardFlightColumn.ATYPE,
        StandardFlightColumnV2.ADEP,
        StandardFlightColumnV2.ADES,
        StandardFlightColumnV2.IOBT,
        StandardFlightColumn.CFMU_TYPE,
        StandardFlightColumnV2.ATOT_TTOT,
        StandardFlightColumn.CTOT,
        StandardFlightColumn.ATOT,
        StandardFlightColumnV2.xTA,
        StandardFlightColumn.MPR,
        StandardFlightColumnV2.DELAY,
        StandardFlightColumn.CONVECTION_IMPACT,
    ];

    state = {
        flights: [],
        selection: [],
    };
    componentDidMount(): void {
        this.refreshFlightList();
    }
    componentDidUpdate(prevProps, prevState, snapshot?: any): void {
        if (
            prevProps !== this.props ||
            prevProps.tvId !== this.props.tvId ||
            prevProps.countType !== this.props.countType ||
            prevProps.trafficWindow !== this.props.trafficWindow ||
            prevProps.trafficType !== this.props.trafficType
        ) {
            this.refreshFlightList();
        }
    }
    refreshFlightList = () => {
        if (
            !this.props.countType ||
            !this.props.tvId ||
            !this.props.trafficWindow?.from ||
            !this.props.trafficWindow?.to
        )
            return undefined;

        const request = {
            clazz: FLIGHT_LIST_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            timeType: TrafficCountUtils.getTimeWindowType(this.props.countType),
            tvName: this.props.tvId,
            dataset: AppContext.getDataset(),
            fields: Utils.copyAndRemoveAllIfNeeded(FlightColumn.computeB2BFieldList(this.SIMPLE_FLIGHT_COLUMNS), [
                StandardFlightFieldType.PROPOSAL_CONTEXT,
                StandardFlightFieldType.PROPOSAL_INFORMATION,
            ]),
            //reply.fields = null;
            includeForecastFlights: true,
            trafficType: this.props.trafficType,
            algorithm: FlightListAlgorithm.NM,
            trafficWindow: this.props.trafficWindow,
        } as FlightListByTvRequestDto;
        ServiceFactory.coreService()
            .getFlightList(request)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    let selection = [];
                    if (this.props.defaultSelectedFlights)
                        selection = reply.data.flights.filter(flight => {
                            return Utils.contains(
                                this.props.defaultSelectedFlights,
                                KeyUtils.FLIGHT_KEY_PROVIDER.getKey(flight),
                                FlightUtils.isSameFlight,
                            );
                        });
                    this.setState({ flights: reply.data.flights, selection: selection });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };
    selection = memoizeOne(
        (selectedFlights: FlightDto[]): MultipleSelection<FlightDto> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItems: selectedFlights,
        }),
    );
    handleSelectionChange = (selection: Array<FlightDto>) => {
        this.setState({ selection: selection });
    };
    handleSelectAll = (selection: Array<FlightDto>) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.state.selection, selection);
        return () => this.handleSelectionChange(newSelection);
    };

    handleClearSelection = () => {
        this.handleSelectionChange([]);
    };
    render() {
        const nbFlights = this.state.flights?.length || 0;
        const nbFlightsSelected = this.state.selection?.length || 0;
        const flightsSelected = nbFlightsSelected > 0;
        return (
            <Flex direction="vertical">
                <Flex size="2em">
                    <Flex size="15em" className="detailed-subpanel-header-left">
                        <SelectionLabel
                            visible={flightsSelected}
                            title={`${nbFlightsSelected} flight${nbFlightsSelected > 1 ? 's' : ''} selected`}
                            subTitle={`${nbFlights} in total`}
                            onSelectAll={this.handleSelectAll(this.state.flights)}
                            onClearSelection={this.handleClearSelection}
                            style={{ marginTop: 0, marginBottom: 0 }}>
                            <div style={{ height: '1em', alignSelf: 'center' }}>
                                <span className="detailed-subpanel-title">FLIGHTS</span>
                                <span className="detailed-subpanel-title-info">{nbFlights}</span>
                            </div>
                        </SelectionLabel>
                    </Flex>
                    <Flex className="detailed-subpanel-header-right">
                        <span>
                            WEF
                            <InputTime value={this.props.trafficWindow?.from} disabled={true} />
                        </span>
                        <span>
                            TIL
                            <InputTime value={this.props.trafficWindow?.to} disabled={true} />
                        </span>
                        <span>
                            Traffic Type
                            <SelectEnum value={this.props.countType} enumType={CountType} disabled={true} />
                        </span>
                        <span>
                            <SelectEnum value={this.props.trafficType} enumType={TrafficType} disabled={true} />
                        </span>
                    </Flex>
                </Flex>
                <Flex style={{ overflowY: 'hidden' }}>
                    <FlightTable
                        data={this.state.flights}
                        columns={this.SIMPLE_FLIGHT_COLUMNS_V2}
                        keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                        noDataMessage={'No active flights in the monitored period'}
                        defaultSorting={[{ columnName: StandardFlightColumn.ENTRY_TIME.header, direction: 'asc' }]}
                        selection={this.selection(this.state.selection)}
                    />{' '}
                    {/*selection={this.selection(this.props.selection)}*/}
                </Flex>
                <Flex size="2.5em" style={{ alignSelf: 'flex-end', paddingTop: '1em' }}>
                    <FlightListButtons
                        selection={this.state.selection}
                        onClose={this.props.onClose}
                        extraButtons={this.props.extraButtons}
                    />
                </Flex>
            </Flex>
        );
    }
}

interface FlightListButtonsProps extends ModalPanelProps {
    selection: Array<FlightDto>;
    extraButtons?: ReactNode[] | RenderFunction | FlightListButtonsRenderFunction;
}

class FlightListButtons extends PureComponent<FlightListButtonsProps> {
    handleAddToSelection = () => {
        AppContext.eventBus.publish(new UpdateFlightSelection('add', this.props.selection));
        this.props.onClose?.();
    };
    handleReplaceSelection = () => {
        AppContext.eventBus.publish(new UpdateFlightSelection('replace', this.props.selection));
        this.props.onClose?.();
    };
    handleFlightDetails = () => {
        this.props.selection.forEach(flight =>
            ModalHandler.showFlightDetailsPanel(flight.flightId, flight.dataset, flight.proposalContext),
        );
    };
    renderExtraButtons = () => {
        if (this.props.extraButtons) {
            if (typeof this.props.extraButtons === 'function') {
                return this.props.extraButtons(this.props.selection);
            } else {
                return this.props.extraButtons;
            }
        }
        return undefined;
    };
    render() {
        const flightsSelected = this.props.selection?.length > 0;
        return (
            <Space>
                {this.renderExtraButtons()}
                <ConditionalRender condition={!AuthorisationUtils.isFmpMapUser()}>
                    <Button key="add-to-selection" disabled={!flightsSelected} onClick={this.handleAddToSelection}>
                        Add to selection
                    </Button>
                    <Button key="replace-selection" disabled={!flightsSelected} onClick={this.handleReplaceSelection}>
                        Replace selection
                    </Button>
                    <Button key="fight-details" disabled={!flightsSelected} onClick={this.handleFlightDetails}>
                        Flight Details
                    </Button>
                </ConditionalRender>
            </Space>
        );
    }
}
